/* eslint-disable react/prop-types */
import React from 'react';
import { Grid, Stack, Card, Tooltip } from '@mui/material';
import { Button, Typography, Box } from 'components/index';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Cell,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  type MRT_VisibilityState,
} from 'material-react-table';
import { Delete, Edit as EditIcon } from '@mui/icons-material';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';
import { usePrompt } from '../AppPrompt';
import { getDivisionColor, compareArrays } from '../report/report.util';
import { toast } from 'react-toastify';
import LineItemsBulkdEditDialog from '../report/LineItemBulkEditDialog';
import { ReportGeneralInfo, ReportLineItemBulkEditFormValues } from '../report/report.type';
import CreateLineItemDialog from '../report/CreateLineItemDialog';
import { REPORT_COLORS, TOTAL_ID } from '../report/constants';
import { Comp, CompLineItem, CreateCompLineItemDTO } from './comp.type';
import WhiteFab from '../common/WhiteFab';
import { createCompLineItem } from './comp.api';
import { handleError } from 'common/utils/util.errors';
import ButtonStyledDropdown from '../common/ButtonStyledDropdown';
import { useDrop, useDrag } from 'react-dnd';
import { DRAG_TYPE } from '../report/ReportDivisionTable';
import { formatCurrency, formatNumber } from '../../common/utils/common.util';

//

interface Props {
  comp: Comp;
  handleDrop: (draggedItem: any, targetDivision: Division | undefined) => void;
  division: Division | null;
  gridData: CompLineItem[];
  divisions: Division[];
  updateComp: (updatedComp: Comp) => void;
  updateLineItem: (updatedLineItem: CompLineItem) => void;
  setSelectedLineItem: (lineItem: CompLineItem | null) => void;
  setLineItemEditDialogOpen: (isOpen: boolean) => void;
  handleOpenProjectDialog: (division: Division) => void;
  refetchComp: () => void;
  isCompDirty: boolean;
  reportGeneralInfo: ReportGeneralInfo;
  hardCostSubTotal?: number;
}

interface CustomToolbarProps {
  headingText: string;
  handleOpenProjectDialog: (division: Division) => void;
}

export const CompDivisionTable: React.FC<Props> = ({
  comp,
  handleDrop,
  updateComp,
  updateLineItem,
  division,
  gridData,
  divisions,
  setLineItemEditDialogOpen,
  setSelectedLineItem,
  handleOpenProjectDialog,
  refetchComp,
  isCompDirty,
  reportGeneralInfo,
  hardCostSubTotal,
}) => {
  const { getConfirmation } = usePrompt();
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = React.useState(false);
  const [isLineItemDialogOpen, setIsLineItemDialogOpen] = React.useState(false);
  const [selectedLineItems, setSelectedLineItems] = React.useState<CompLineItem[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<MRT_VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [isOver, setIsOver] = React.useState(false);

  React.useEffect(() => {
    const ids = Object.keys(rowSelection);
    const selected = gridData.filter(li => ids.includes(li.id));
    setSelectedLineItems(selected);
  }, [rowSelection, gridData]);

  const [, dropRef] = useDrop(() => ({
    accept: DRAG_TYPE,

    drop: (item: { divisionId: string; selectedRows?: CompLineItem[]; fullRow?: CompLineItem }) => {
      if (item.divisionId !== division?.id && handleDrop) {
        handleDrop(item, division || undefined);
      }
    },

    collect: monitor => {
      const isCurrentlyOver = monitor.isOver({ shallow: true });

      setIsOver(isCurrentlyOver);
      return {};
    },
  }));

  const processRowUpdate = (updatedRow: any, originalRow: any) => {
    const same = compareArrays(updatedRow, originalRow);
    if (!same) updateLineItem(updatedRow);
    return { ...updatedRow };
  };

  const handleOpenLineItemDialog = () => {
    setIsLineItemDialogOpen(true);
  };

  const handleCloseLineItemDialog = () => {
    setIsLineItemDialogOpen(false);
  };

  const saveLineItem = async (lineItemData: CreateCompLineItemDTO) => {
    try {
      lineItemData.compId = comp.id;
      if (isCompDirty) {
        const isConfirmed = await getConfirmation({
          description: 'Creating a new LineItem will overwrite any unsaved changes in the Comparable. Are you sure you want to continue?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
      }
      await createCompLineItem(comp.id, lineItemData);
      toast.success('Line Item added successfully!');
      refetchComp();
    } catch (error) {
      handleError(error);
    }
  };

  const handleBulkDelete = React.useCallback(async () => {
    const selectedIds = selectedLineItems.map(li => li.id);
    if (selectedIds.length === 0) {
      toast.error('Please select at least one row!');
      return;
    }
    const confirmed = await getConfirmation({ description: 'Are you sure you want to delete these line items?' });
    if (!confirmed) return;
    const updatedLineItems = comp.lineItems.map(lineItem => {
      if (selectedIds.includes(lineItem.id)) {
        return { ...lineItem, isDeleted: true };
      }
      return lineItem;
    });
    updateComp({ ...comp, lineItems: updatedLineItems });
  }, [selectedLineItems, comp, updateComp]);

  const handleDeleteReportLineItem = React.useCallback(
    async (id: string) => {
      const confirmed = await getConfirmation({ description: 'Are you sure you want to delete this line item?' });
      if (!confirmed) return;
      const updatedLineItems = comp.lineItems.map(lineItem => {
        if (lineItem.id === id) {
          return { ...lineItem, isDeleted: true };
        }
        return lineItem;
      });
      updateComp({ ...comp, lineItems: updatedLineItems });
    },
    [getConfirmation, comp],
  );
  const handleBulkEditClick = React.useCallback(() => {
    if (selectedLineItems.length === 0) {
      toast.error('Please select at least one row!');
      return;
    }
    setBulkEditDialogOpen(true);
  }, [selectedLineItems]);

  const handleBulkEditSubmit = React.useCallback(
    (values: ReportLineItemBulkEditFormValues) => {
      if (selectedLineItems.length === 0) {
        toast.error('Failed to update. No line items found!');
        return;
      }
      const updatedLineItems = comp.lineItems.map(lineItem => {
        if (selectedLineItems.find(li => li.id === lineItem.id)) {
          return { ...lineItem, ...values, isEdited: true };
        }
        return lineItem;
      });
      updateComp({ ...comp, lineItems: updatedLineItems });
      setBulkEditDialogOpen(false);
    },
    [selectedLineItems, comp, updateComp],
  );
  const renderActionsHeader = React.useCallback(
    () => (
      <Stack direction={'row'} spacing={1} padding={1}>
        <WhiteFab
          aria-label='edit'
          icon={<EditIcon fontSize='small' />}
          textColor='primary'
          onClick={handleBulkEditClick}
          size='small'
          disabled={selectedLineItems.length == 0}
        />
        <WhiteFab
          aria-label='delete'
          icon={<Delete fontSize='small' />}
          textColor='error'
          onClick={handleBulkDelete}
          size='small'
          disabled={selectedLineItems.length == 0}
        />
      </Stack>
    ),
    [handleBulkEditClick, handleBulkDelete, selectedLineItems],
  );
  const renderActionsCell = React.useCallback(
    (params: any) =>
      params.id == TOTAL_ID ? null : (
        <Stack direction={'row'} spacing={1} padding={1}>
          <WhiteFab
            aria-label='edit'
            icon={<EditIcon fontSize='small' />}
            textColor='primary'
            onClick={(e: any) => {
              e.stopPropagation();
              setSelectedLineItem(params.row);
              setLineItemEditDialogOpen(true);
            }}
            size='small'
            disabled={selectedLineItems.length > 0}
          />
          <WhiteFab
            aria-label='delete'
            icon={<Delete fontSize='small' />}
            textColor='error'
            onClick={(e: any) => {
              e.stopPropagation();
              handleDeleteReportLineItem(params.row.id);
            }}
            size='small'
            disabled={selectedLineItems.length > 0}
          />
        </Stack>
      ),
    [handleDeleteReportLineItem, selectedLineItems],
  );

  const handleMoveLineItemsToDivision = (targetDivisionId: string) => {
    if (!targetDivisionId || !selectedLineItems.length) return;

    const assignedDivision = divisions.find(div => div.id === targetDivisionId);
    const updatedLineItems = comp.lineItems.map(lineItem => {
      if (selectedLineItems.find(li => li.id === lineItem.id)) {
        return { ...lineItem, division: assignedDivision || null, isEdited: true };
      }
      return lineItem;
    });
    updateComp({ ...comp, lineItems: updatedLineItems });
    setSelectedLineItems([]);
  };

  // MRT: build columns for comp table
  const unitsList = React.useMemo(
    () => [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }],
    [],
  );

  const filteredLineItems = React.useMemo(() => gridData.filter(item => !item.isDeleted), [gridData]);

  const totals = React.useMemo(() => {
    const totalBudget = filteredLineItems.reduce((acc, item) => acc + (item.totalBudget || 0), 0);
    const grossBuildingSF = comp?.grossBuildingSF || 0;
    const quantity = grossBuildingSF > 0 ? totalBudget / grossBuildingSF : 0;
    return {
      description: `${division?.name?.toLocaleUpperCase() ?? 'NO DIVISION ASSIGNED'} TOTALS`,
      totalBudget,
      quantity,
    };
  }, [filteredLineItems, comp?.grossBuildingSF, division]);

  const handleSaveCell = (cell: MRT_Cell<CompLineItem>, value: any) => {
    const updatedData = { ...cell.row.original } as any;
    const columnId = cell.column.id;
    if (['totalBudget', 'quantity'].includes(columnId)) {
      const parsed = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) || 0 : (value ?? 0);
      updatedData[columnId] = parsed;
    } else if (columnId === 'unit') {
      updatedData.unit = value || '';
    }
    processRowUpdate(updatedData, cell.row.original);
  };

  const mrtColumns = React.useMemo<MRT_ColumnDef<CompLineItem>[]>(() => {
    const DraggableDescriptionCell: React.FC<{ cell: MRT_Cell<CompLineItem>; row: any }> = ({ cell, row }) => {
      const description = cell.getValue<string>();
      const [{ isDragging }, dragRef] = useDrag(() => ({
        type: DRAG_TYPE,
        item: () => {
          const selectedIds = Object.keys(rowSelection);
          const selected = filteredLineItems.filter(li => selectedIds.includes(li.id));
          const isRowSelected = selectedIds.includes(row.original.id);
          return {
            id: row.original.id,
            divisionId: row.original.division?.id,
            selectedRows: selected.length > 1 && isRowSelected ? selected : undefined,
            fullRow: selected.length > 1 && isRowSelected ? undefined : row.original,
          };
        },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
      }));

      return (
        <div
          ref={dragRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            overflow: 'hidden',
            cursor: 'move',
            opacity: isDragging ? 0.5 : 1,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{description}</span>
        </div>
      );
    };

    const leftAlignedProps = {
      muiTableBodyCellProps: { align: 'left' as const },
      muiTableHeadCellProps: {
        align: 'left' as const,
        sx: (theme: any) => ({
          backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
          color: '#000000',
          fontWeight: '500',
          padding: '8px 16px',
          height: '52px',
          minHeight: '52px',
          border: 'none',
          position: 'relative',
          justifyContent: 'flex-start',
          verticalAlign: 'middle',
          '& .Mui-TableHeadCell-Content-Wrapper': {
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
            display: 'flex',
          },
          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)',
          },
        }),
      },
      muiTableFooterCellProps: { align: 'left' as const },
    };

    const cols: Omit<MRT_ColumnDef<CompLineItem>, 'size'>[] = [
      {
        accessorKey: 'lineItem.description',
        header: 'Description',
        Cell: ({ cell, row }) => {
          const isTotalRow = row.id === TOTAL_ID;
          if (isTotalRow) return <span style={{ fontWeight: 'bold' }}>{cell.getValue<string>()}</span>;
          return <DraggableDescriptionCell cell={cell} row={row} />;
        },
        Footer: () => totals.description,
        enableEditing: false,
      },
      {
        accessorKey: 'totalBudget',
        header: 'Total Budget',
        enableEditing: true,
        Cell: ({ cell }) => formatCurrency((cell.getValue<number>() ?? 0).toString(), true),
        muiEditTextFieldProps: ({ cell }) => ({
          sx: { '& .MuiInputBase-input': { textAlign: 'left' } },
          onBlur: event => handleSaveCell(cell, event.target.value),
        }),
        ...leftAlignedProps,
        Footer: () => formatCurrency(totals.totalBudget.toString(), true),
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          const isTotal = row.id === TOTAL_ID;
          if (isTotal) return formatCurrency((value ?? 0).toString(), true);
          return value && typeof value === 'number' && !isNaN(value) ? formatNumber(value, 2) : '';
        },
        muiEditTextFieldProps: ({ cell }) => ({
          sx: { '& .MuiInputBase-input': { textAlign: 'left' } },
          onBlur: event => handleSaveCell(cell, event.target.value),
        }),
        ...leftAlignedProps,
        Footer: () => formatCurrency(totals.quantity.toString(), true),
      },
      {
        accessorKey: 'unit',
        header: 'Unit',
        Cell: ({ cell }) => cell.getValue<string>() || '',
        Edit: ({ cell, table }) => {
          const handleChange = (unit: string) => {
            handleSaveCell(cell as MRT_Cell<CompLineItem>, unit);
            table.setEditingCell(null);
          };
          return (
            <select
              value={cell.getValue<string>() || ''}
              onChange={e => handleChange(e.target.value)}
              style={{ width: '100%', padding: '8px' }}
            >
              {unitsList.map(u => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          );
        },
        enableEditing: true,
        ...leftAlignedProps,
        Footer: () => '/SF',
      },
      {
        accessorKey: 'unitCost',
        header: '$ / Unit',
        Cell: ({ row }) => {
          const li = row.original;
          const unitCost = li.quantity > 0 ? (li.totalBudget || 0) / (li.quantity || 0) : 0;
          return unitCost && unitCost !== 0 ? formatCurrency(unitCost.toString()) : '--';
        },
        ...leftAlignedProps,
        enableEditing: false,
        Footer: () => '--',
      },
    ];

    const sized = cols.map(col => {
      const columnId = col.accessorKey ?? col.id ?? '';
      let minSize = 100;
      if (columnId === 'lineItem.description') minSize = 250;
      if (columnId === 'unit') minSize = 80;
      if (columnId === 'quantity') minSize = 120;
      if (columnId === 'unitCost') minSize = 120;
      if (columnId === 'totalBudget') minSize = 180;
      return { ...col, minSize } as MRT_ColumnDef<CompLineItem>;
    });

    return sized;
  }, [division, totals, unitsList]);
  const CustomToolbar: React.FC<CustomToolbarProps> = ({ headingText, handleOpenProjectDialog }) => {
    // ActionsMenu component is used here
    const ActionsMenu = React.useCallback(() => {
      return (
        <Stack margin={'auto'} direction={'row'} spacing={1}>
          <ButtonStyledDropdown
            divisions={divisions}
            selectedLineItems={selectedLineItems}
            division={division}
            isLocked={false}
            onSelect={handleMoveLineItemsToDivision}
          />
          {division ? (
            <>
              <Button
                onClick={() => {
                  handleOpenProjectDialog(division);
                }}
                variant='outlined'
              >
                Manage Budgets
              </Button>
              <Button onClick={handleOpenLineItemDialog} variant='outlined'>
                Add Line Item
              </Button>
            </>
          ) : null}
        </Stack>
      );
    }, [handleBulkDelete, handleBulkEditClick, handleOpenProjectDialog, division]);

    return (
      <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} p={2}>
        <Stack direction={'row'} spacing={2}>
          <Typography color='black' variant='h6'>
            {headingText}
          </Typography>
        </Stack>
        <Stack direction={'row'} spacing={2}>
          <ActionsMenu />
        </Stack>
      </Stack>
    );
  };

  return (
    <React.Fragment key={division?.id ?? 'No-Division'}>
      <Grid size={{ xs: 12 }} ref={dropRef}>
        <Card
          elevation={1}
          sx={{
            padding: '0px 10px 10px 10px',
            border: isOver ? '2px solid #0283a0' : '2px solid transparent',
            transition: 'border 0.2s ease-in-out',
          }}
        >
          <Box>
            <CustomToolbar
              headingText={division ? `${division.code} ${division.name.toLocaleUpperCase()}` : 'NO DIVISION ASSIGNED'}
              handleOpenProjectDialog={handleOpenProjectDialog}
            />
            <Box sx={{ overflowX: 'auto', px: 1 }}>
              <Box sx={{ minWidth: '100%' }}>
                <MaterialReactTable
                  table={useMaterialReactTable({
                    columns: mrtColumns,
                    data: filteredLineItems,
                    getRowId: row => row.id,
                    initialState: {
                      density: 'compact',
                      columnPinning: { left: ['mrt-row-select', 'lineItem.description'], right: ['mrt-row-actions'] },
                    },
                    layoutMode: 'grid',
                    enableColumnResizing: true,
                    enableColumnPinning: true,
                    columnResizeMode: 'onChange',
                    enableRowSelection: true,
                    enableMultiRowSelection: true,
                    enablePagination: false,
                    enableDensityToggle: false,
                    enableColumnActions: false,
                    editDisplayMode: 'cell',
                    enableEditing: true,
                    onRowSelectionChange: setRowSelection,
                    onColumnVisibilityChange: setColumnVisibilityModel,
                    positionActionsColumn: 'last',
                    enableRowActions: true,
                    renderRowActions: ({ row }) => (
                      <Stack direction='row' spacing={1} alignItems='flex-end' justifyContent='center'>
                        <Tooltip title='Edit Line Item' arrow={false}>
                          <span>
                            <WhiteFab
                              size='small'
                              sx={{ width: 32, height: 32, minHeight: 32 }}
                              icon={<EditIcon fontSize='small' />}
                              textColor='primary'
                              onClick={() => {
                                setSelectedLineItem(row.original);
                                setLineItemEditDialogOpen(true);
                              }}
                              disabled={Object.keys(rowSelection).length > 0}
                            />
                          </span>
                        </Tooltip>
                        <Tooltip title='Delete Line Item' arrow={false}>
                          <span>
                            <WhiteFab
                              size='small'
                              sx={{ width: 32, height: 32, minHeight: 32 }}
                              icon={<Delete fontSize='small' />}
                              textColor='error'
                              onClick={() => handleDeleteReportLineItem(row.original.id)}
                              disabled={Object.keys(rowSelection).length > 0}
                            />
                          </span>
                        </Tooltip>
                      </Stack>
                    ),
                    displayColumnDefOptions: {
                      'mrt-row-actions': {
                        header: '',
                        size: 80,
                        Header: ({ table }) => (
                          <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
                            <Tooltip title='Edit Selected Line Items' arrow={false}>
                              <span>
                                <WhiteFab
                                  size='small'
                                  sx={{ width: 32, height: 32, minHeight: 32 }}
                                  icon={<EditIcon fontSize='small' />}
                                  textColor='primary'
                                  onClick={handleBulkEditClick}
                                  disabled={table.getSelectedRowModel().flatRows.length === 0}
                                />
                              </span>
                            </Tooltip>
                            <Tooltip title='Delete Selected Line Items' arrow={false}>
                              <span>
                                <WhiteFab
                                  size='small'
                                  sx={{ width: 32, height: 32, minHeight: 32 }}
                                  icon={<Delete fontSize='small' />}
                                  textColor='error'
                                  onClick={handleBulkDelete}
                                  disabled={table.getSelectedRowModel().flatRows.length === 0}
                                />
                              </span>
                            </Tooltip>
                          </Stack>
                        ),
                      },
                    },
                    muiTableContainerProps: { sx: { padding: '0px' } },
                    muiTableBodyRowProps: ({ row }) => {
                      let backgroundColor = '';
                      if (row.original?.isEdited) backgroundColor = REPORT_COLORS.UNSAVED_BACKGROUND_COLOR;
                      if (!backgroundColor) backgroundColor = row.index % 2 === 0 ? '#ffffff' : '#f9f9f9';
                      return {
                        sx: {
                          cursor: 'pointer',
                          backgroundColor: backgroundColor + ' !important',
                          border: 'none',
                          '& td[data-pinned="true"]::before': { backgroundColor },
                        },
                      };
                    },
                    state: { rowSelection, columnVisibility: columnVisibilityModel },
                    muiTableHeadProps: { sx: { padding: 0, color: 'white' } },
                    muiTableHeadCellProps: {
                      sx: theme => ({
                        backgroundColor: getDivisionColor(division?.code ?? 'no-division') + ' !important',
                        color: '#000000',
                        fontWeight: '500',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        height: '52px',
                        minHeight: '52px',
                        border: 'none',
                        lineHeight: '44px',
                        verticalAlign: 'middle',
                        '& .Mui-TableHeadCell-Content-Wrapper': {
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                          display: 'flex',
                        },
                        '&[data-pinned="true"]:not([role="columnheader"])::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
                          zIndex: 0,
                        },
                        '&[data-pinned="true"]:not([role="columnheader"])': { zIndex: 1 },
                        '&:not(:last-of-type)::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '20%',
                          height: '60%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.15)',
                          boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)',
                        },
                      }),
                    },
                    muiTableBodyCellProps: { sx: { border: 'none' } },
                    muiTableFooterCellProps: {
                      sx: theme => ({
                        backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
                        color: '#000000',
                        fontWeight: '500',
                        padding: '8px 16px',
                        border: 'none',
                        '&[data-pinned="true"]::before': {
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          right: 0,
                          backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
                          zIndex: 0,
                        },
                        '&[data-pinned="true"]': { zIndex: 1 },
                        '&:not(:last-of-type)::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '20%',
                          height: '60%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.15)',
                          boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)',
                        },
                      }),
                    },
                    muiTablePaperProps: { sx: { boxShadow: 'none' } },
                    muiTopToolbarProps: {
                      sx: {
                        backgroundColor: 'white !important',
                        justifyContent: 'unset !important',
                        display: 'flex',
                        '& .css-zrlv9q': { justifyContent: 'unset !important' },
                        '& .MuiToolbar-root.MuiToolbar-gutters.css-1613k89-MuiToolbar-root': { display: 'none' },
                      },
                    },
                    renderTopToolbarCustomActions: () => <Box />,
                  })}
                />
              </Box>
            </Box>
          </Box>
        </Card>
      </Grid>
      <LineItemsBulkdEditDialog
        open={bulkEditDialogOpen}
        onClose={() => {
          setBulkEditDialogOpen(false);
        }}
        divisions={divisions}
        onSubmit={handleBulkEditSubmit}
      />
      <CreateLineItemDialog
        open={isLineItemDialogOpen}
        onClose={handleCloseLineItemDialog}
        currentDivision={division}
        divisions={divisions}
        saveLineItem={saveLineItem}
        reportGeneralInfo={reportGeneralInfo}
      />
    </React.Fragment>
  );
};

export default CompDivisionTable;
